import React, { ReactElement, useCallback, useEffect, Fragment, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom';

import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useAsyncDebounce,
    Column as ColumnType,
    TableOptions,
    UseSortByColumnOptions,
    UseFiltersColumnOptions,
} from 'react-table';
import { Paginated, PaginatedBaseMeta } from 'src/services/api-handlers/pagination';
import Pagination from 'src/components/react-table/Pagination';
import { QueryParams } from 'src/types/grid';
import styles from 'src/components/react-table/assets/grid.module.scss';
import textFilter from 'src/components/react-table/filters/text-filter';
import useMemoCompare from 'src/components/react-table/hooks/memo-compare';
import Loader from 'src/components/Loader';

import Dotdotdot from 'react-dotdotdot';
import { DateTimePicker, DropdownList } from 'react-widgets';
import CaseEnum from 'src/enumerables/case-enum';
import OrderEnum from 'src/enumerables/order-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import user from 'src/services/user-auth';
import UserRole from 'src/enumerables/user-role';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';
import { getServiceTypes } from 'src/store/actions/service-type-actions';
import { IServiceType } from 'src/types/service-types';
import { useDispatch } from 'react-redux';
import Multiselect from 'multiselect-react-dropdown';
import { provider } from '../../../../../../../helpers/provider';
import { useCookies } from 'react-cookie';

type ColumnAdd = {
    headerClassName?: string;
    noDotsRow?: boolean;
};

export type Column<T extends Record<string, unknown>> = ColumnType<T> &
    UseSortByColumnOptions<T> &
    ColumnAdd &
    UseFiltersColumnOptions<T>;

interface Props<T extends Record<string, unknown>> {
    columns: Column<T>[];
    columnOpen?: boolean;
    setColumnOpen?: (open: boolean) => void;
    noLoader?: boolean;
    filtersInit?: any;
    filterGlobal?: boolean;
    data: Paginated<T, PaginatedBaseMeta>;
    getData: (params: QueryParams) => void;
    getDataCsv?: (params: QueryParams) => void;
    noDots?: boolean;
    classNameGrid?: string;
    inProgressCSV?: boolean;
    serviceTypes?: any;
    providerId?: any;
}

const defaultColumn = {
    Filter: textFilter(),
};
function GlobalFilter({ value, setValue, serviceTypes }: any) {
    const onChange = useAsyncDebounce((change) => {

        setValue({ ...value, [change.id]: change.value });
    }, 200);

    const onSelect = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceType', value: selectedList });
    };

    const onRemove = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceType', value: selectedList });
    };

    const onSelectProvider = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceTypeProvider', value: selectedList });
    };

    const onRemoveProvider = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceTypeProvider', value: selectedList });
    };

    const dispatch = useDispatch();

    useEffect(() => {
        if (serviceTypes.list.length == 0) {
            serviceTypes = dispatch(
                getServiceTypes({
                    page: 1,
                    itemsPerPage: 1200,
                }),
            );
        }
    }, []);

    const serviceTypesValues: any = [];

    serviceTypes.list.forEach((stype: Partial<IServiceType>) => {
        const labelArr = [];
        if (stype.title) {
            labelArr.push(stype.title);
        }
        if (stype.cpt) {
            labelArr.push(`CPT: [${stype.cpt}]`);
        }
        const label = labelArr.join(', ');
        serviceTypesValues.push({ value: stype.id, cpt: `${stype.cpt ? stype.cpt : ''}`, label });
    });

    const style = {
        chips: {
            background: '#3e81b9',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
        },
        searchBox: {
            cursor: 'pointer',
        },
        multiselectContainer: {
            color: '#3e81b9',
            zIndex: '15',
        },
        optionContainer: {
            zIndex: '15',
        },
        option: {
            background: 'white',
            color: '#3e81b9',
        },
    };

    const caseEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
    const caseTypes: SelectObject[] = getValFromEnum(caseEnum);

    const serviceTypeProviderEnum = OrderEnum ? OrderEnum.getEnum('ServiceTypeProvider') : {};
    const serviceTypeProviders: SelectObject[] = getValFromEnum(serviceTypeProviderEnum);

    return (
        <div>
            <div className={classNames(styles.firstFilterBlock)}>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span>From:</span>
                    <DateTimePicker
                        value={value && value.dateFrom ? moment(value.dateFrom).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateFrom', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span>To: </span>
                    <DateTimePicker
                        value={value && value.dateTo ? moment(value.dateTo).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateTo', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                    <span>Case Type: </span>
                    <DropdownList
                        data={[{ label: 'All', value: '' }, ...caseTypes]}
                        containerClassName={'w-100'}
                        textField="label"
                        valueField="value"
                        onChange={(item: SelectObject) => {
                            onChange({ id: 'caseType', value: item.value ? item.value : undefined });
                        }}
                        value={value && value.caseType ? { value: value.caseType, label: '' } : undefined}
                    />
                </div>
                <br />
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                    <span>Service Type Provider: </span>
                    <Multiselect
                        options={[...serviceTypeProviders]} // Options to display in the dropdown
                        selectedValues={value && value.serviceTypeProvider ? value.serviceTypeProvider : undefined} // Preselected value to persist in dropdown
                        onSelect={onSelectProvider} // Function will trigger on select event
                        onRemove={onRemoveProvider} // Function will trigger on remove event
                        displayValue="label" // Property name to display in the dropdown options
                        closeIcon="close"
                        placeholder=""
                        style={style}
                        emptyRecordMsg=""
                        hidePlaceholder={true}
                        showArrow={true}
                    />
                </div>
                {serviceTypesValues ? (
                    <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                        <span>Service Type: </span>
                        <Multiselect
                            options={[...serviceTypesValues]} // Options to display in the dropdown
                            selectedValues={value && value.serviceType ? value.serviceType : undefined} // Preselected value to persist in dropdown
                            onSelect={onSelect} // Function will trigger on select event
                            onRemove={onRemove} // Function will trigger on remove event
                            displayValue="label" // Property name to display in the dropdown options
                            closeIcon="close"
                            placeholder=""
                            style={style}
                            emptyRecordMsg=""
                            hidePlaceholder={true}
                            showArrow={true}
                        />
                    </div>
                ) : (
                    <div></div>
                )}
            </div>
        </div>
    );
}
function Grid<T extends Record<string, unknown>>(props: Props<T>): ReactElement {
    const [globalFilters, setGlobalFilters] = React.useState({
        dateFrom: moment().startOf('month').format('YYYY-MM-DD'),
        dateTo: moment().endOf('month').format('YYYY-MM-DD'),
        serviceType: [],
        serviceTypeValues: [],
        serviceTypeProvider: [],
        serviceTypeProviderValues: [],
    });
    const {
        columns,
        data,
        getData,
        columnOpen,
        setColumnOpen,
        filtersInit = [],
        getDataCsv,
        inProgressCSV,
        noLoader,
        noDots,
        classNameGrid,
        serviceTypes,
        providerId,
    } = props;
    const match: any = useRouteMatch();
    const [cookiesDateFrom, setCookieDateFrom] = useCookies(['fromDate']);
    const [cookiesDateTo, setCookieDateTo] = useCookies(['toDate']);
    // const filtersInit: any = [];

    useEffect(() => {
        if ((Object.values(match.params).length && match.params.status) || match.params.status) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
    }, []); // eslint-disable-line

    const instance = useTable<T>(
        {
            columns,

            defaultColumn,
            data: data.list,
            manualSortBy: true,
            filterTypes: true,
            manualFilters: true,
            manualPagination: true,
            pageCount: Math.max(1, data.totalPages ?? 1),
            initialState: {
                pageIndex: data.page - 1,
                pageSize: 20,
                filters: filtersInit,
            },
        } as TableOptions<T>,
        useFilters,

        useSortBy,
        usePagination,
    );
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = instance;

    const { sortBy, filters, pageIndex, pageSize } = state as any;
    const getPreparedFilters = useCallback(() => {
        const result: any = {};
        filters.forEach((filter: { id: string; value: any }) => {
            result[filter.id] = filter.value;
        });

        return result;
    }, [filters]);
    const getPreparedSorting = useCallback(() => {
        const result: any = {};
        sortBy.forEach((sort: { id: string; desc: boolean }) => {
            result[sort.id] = sort.desc ? 'desc' : 'asc';
        });

        return result;
    }, [sortBy]);
    const updatedAt = useMemoCompare(Date.now(), () => {
        return data.meta?.updatedAt !== 0;
    });

    useEffect(() => {
        const serviceTypeFilters: any = [];
        globalFilters?.serviceType?.map((item: any, i: number) => {
            serviceTypeFilters.push(item.value);
        });
        const serviceTypeProviderFilters: any = [];
        globalFilters?.serviceTypeProvider?.map((item: any, i: number) => {
            serviceTypeProviderFilters.push(item.value);
        });

        globalFilters.serviceTypeValues = serviceTypeFilters;
        globalFilters.serviceTypeProviderValues = serviceTypeProviderFilters;

        const allSelectedFilters: any = { ...getPreparedFilters(), ...globalFilters };
        if (allSelectedFilters['serviceTypeProvider.id']) {
            allSelectedFilters.serviceTypeProvider = allSelectedFilters['serviceTypeProvider.id'];
        }

        const pf = getPreparedFilters();
        if (pf) {
            allSelectedFilters.serviceTypeProvider = pf?.serviceTypeProvider ? pf?.serviceTypeProvider : [];
            allSelectedFilters.serviceType = pf?.serviceType ? pf?.serviceType : [];
        }
        allSelectedFilters['provider.id'] = providerId;

        if (cookiesDateFrom.fromDate !== 'undefined' && cookiesDateTo.toDate !== 'undefined') {
            allSelectedFilters['seanceDate'] = {
                'seanceDate[after]': cookiesDateFrom.fromDate
                    ? moment(cookiesDateFrom.fromDate).format('MMM D, YYYY')
                    : '',
                'seanceDate[before]': cookiesDateTo.toDate ? moment(cookiesDateTo.toDate).format('MMM D, YYYY') : '',
            };
        }

        getData({
            page: pageIndex + 1,
            itemsPerPage: pageSize,
            filters: allSelectedFilters,
            sorting: getPreparedSorting(),
        });
    }, [getData, sortBy, filters, pageIndex, pageSize, updatedAt, globalFilters]); // eslint-disable-line

    const isProvider = user.getRoles().includes(UserRole.ROLE_USER);
    const isSupervisor = user.getSupervisor();

    return (
        <div className={'position-relative'}>
            {getDataCsv ? (
                isProvider && !isSupervisor ? null : (
                    <button
                        className={classNames(styles.btnHeader, 'btn btn-outline-info')}
                        onClick={() => {

                            const serviceTypeFilters: any = [];
                            globalFilters?.serviceType?.map((item: any, i: number) => {
                                serviceTypeFilters.push(item.value);
                            });
                            const serviceTypeProviderFilters: any = [];
                            globalFilters?.serviceTypeProvider?.map((item: any, i: number) => {
                                serviceTypeProviderFilters.push(item.value);
                            });

                            globalFilters.serviceTypeValues = serviceTypeFilters;
                            globalFilters.serviceTypeProviderValues = serviceTypeProviderFilters;

                            const allSelectedFilters: any = { ...getPreparedFilters(), ...globalFilters };
                            if (allSelectedFilters['serviceTypeProvider.id']) {
                                allSelectedFilters.serviceTypeProvider = allSelectedFilters['serviceTypeProvider.id'];
                            }

                            const pf = getPreparedFilters();
                            if (pf) {
                                allSelectedFilters.serviceTypeProvider = pf?.serviceTypeProvider ? pf?.serviceTypeProvider : [];
                                allSelectedFilters.serviceType = pf?.serviceType ? pf?.serviceType : [];
                            }
                            allSelectedFilters['provider.id'] = providerId;

                            if (cookiesDateFrom.fromDate !== 'undefined' && cookiesDateTo.toDate !== 'undefined') {
                                allSelectedFilters['seanceDate'] = {
                                    'seanceDate[after]': cookiesDateFrom.fromDate
                                        ? moment(cookiesDateFrom.fromDate).format('MMM D, YYYY')
                                        : '',
                                    'seanceDate[before]': cookiesDateTo.toDate ? moment(cookiesDateTo.toDate).format('MMM D, YYYY') : '',
                                };
                            }

                            getDataCsv({
                                page: pageIndex + 1,
                                filters: {
                                    ...allSelectedFilters,
                                    //...globalFilters,
                                    ...{ 'provider.id': providerId },
                                },
                                sorting: getPreparedSorting(),
                            });
                        }}
                    >
                        {inProgressCSV ? 'loading...' : 'Export .csv'}{' '}
                    </button>
                )
            ) : null}
            {props.filterGlobal && (
                <GlobalFilter value={globalFilters} setValue={setGlobalFilters} serviceTypes={serviceTypes} />
            )}
            <div
                style={noDots ? {} : { minHeight: '450px' }}
                className={classNames(styles.table, 'table-responsive', classNameGrid)}
            >
                {data.inProgress && !noLoader ? <Loader /> : null}
                <table className={classNames('table')} {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup, ind) => (
                            <Fragment key={ind}>
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column: any, i: number) => {
                                        if (i === headerGroup.headers.length - 1 && setColumnOpen) {
                                            return (
                                                <th
                                                    style={{ position: 'relative' }}
                                                    key={i}
                                                    {...column.getHeaderProps()}
                                                    width={column.width}
                                                >
                                                    <span style={{ position: 'absolute', bottom: '5px' }}>
                                                        {column.render('Header')}
                                                    </span>
                                                    <button
                                                        style={{
                                                            float: 'right',
                                                        }}
                                                        className={'btn btn-info ml-3'}
                                                        onClick={() => {
                                                            setColumnOpen(!columnOpen);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={columnOpen ? faAngleDoubleLeft : faAngleDoubleRight}
                                                        />
                                                    </button>
                                                </th>
                                            );
                                        }
                                        return (
                                            <th key={i} {...column.getHeaderProps()} width={column.width}>
                                                {column.canSort ? (
                                                    <span
                                                        className={column.headerClassName}
                                                        {...column.getSortByToggleProps()}
                                                    >
                                                        {column.render('Header')}
                                                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                    </span>
                                                ) : (
                                                    <span>{column.render('Header')}</span>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    {headerGroup.headers.map((column: any, i: number) => (
                                        <th key={i} {...{ width: column.width }}>
                                            {column.canFilter ? column.render('Filter') : null}
                                        </th>
                                    ))}
                                </tr>
                            </Fragment>
                        ))}
                    </thead>
                    {noDots ? (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell, j: number) => {
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    ) : (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell: any, j: number) => {
                                                //:Cell<T,any>

                                                const noDots: any = cell.column.noDotsRow;
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {noDots ? (
                                                            cell.render('Cell')
                                                        ) : (
                                                            <Dotdotdot clamp={3}>{cell.render('Cell')}</Dotdotdot>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
                <Pagination<T> table={instance} />
            </div>
        </div>
    );
}

export default Grid;
